// @flow
import * as React from 'react';
import { useDebouncedMemo, useDeviceWidth } from '@coverright/utils';
import { useLayoutEffect, useRef } from 'react';
import { DrugsQuery, useDrugsQuery } from '@coverright/data-access/medicare';
import { TextInput } from '@coverright/ui/inputs';
import { IconButton, InputAdornment, ListItemButton, ListItemText } from '@mui/material';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { V3InfoPanel } from '@coverright/ui/shared';

type Props = {
  onSelect: (drug: DrugsQuery['drugs'][0]) => void;
};

export function DrugSelectionModalSearch({ onSelect }: Props) {
  const phone = useDeviceWidth('sm')
  const ref = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const deferredValue = useDebouncedMemo(searchTerm, 500);
  const { data, loading } = useDrugsQuery({
    variables: { name: deferredValue },
    skip: !deferredValue,
  });

  useLayoutEffect(() => {
    ref.current?.focus()
  }, [])

  const drugs = data?.drugs || [];

  return (
    <div>
      <TextInput value={searchTerm}
                 onChange={event => setSearchTerm(event.target.value)}
                 placeholder={'Search by name...'}
                 data-test={'search-drug-input'}
                 hideTick
                 inputRef={ref}
                 sx={{mb: 2}}
                 validate={false}
                 loading={loading}
                 InputProps={{
                   startAdornment: <InputAdornment position="start">
                     <MagnifyingGlass size={20} color={'#1C434F'} />
                   </InputAdornment>,
                   endAdornment: searchTerm && !loading && <InputAdornment position="end">
                     <IconButton size={'small'} onClick={() => { setSearchTerm(''); ref.current?.focus() }}>
                       <X size={20} color={'#1C434F'} />
                     </IconButton>
                   </InputAdornment>
                 }}
      />

      {drugs?.length > 0 && (
        <OverlayScrollbarsComponent
          style={{maxHeight: 400}}
          defer
        >
          {drugs.map(drug => (
            <ListItemButton key={drug.name} data-test={'drug-option-' + drug.name} onClick={() => onSelect(drug)}>
              <ListItemText primaryTypographyProps={{sx: {fontWeight: 600} }}
                            primary={drug.name} />
            </ListItemButton>
          ))}
        </OverlayScrollbarsComponent>
      )}


      {drugs.length === 0 && <V3InfoPanel
        sx={{ backgroundColor: '#F0F7FA' }}
        className={'find-drug-helper'}
        title={'Can’t find your drug?'}
        showIcon={!phone}
        text={
          'Not all drugs are covered by Medicare plans.  If you can’t find a prescription drug that’s important to you, continue entering the ones available and then speak to one of our licensed insurance agent.  However, there may not be a plan in your area that covers all your drugs.'
        } />}
    </div>
  );
}
